import React, { useCallback, useEffect } from 'react'
import { connect, useSelector } from 'react-redux'

import { noop } from 'lodash-es'

import { MIXPANEL_PROPERTY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { AppContext } from '@lyrahealth-inc/ui-core-crossplatform/src/context'

import { useSendExperimentData } from '../../common/hooks/useSendExperimentData'
import { useShouldShowAICoach } from '../../common/hooks/useShouldShowAICoach'
import { useUpdateLDContext } from '../../common/hooks/useUpdateLDContext'
import { getDetectedCountryISO } from '../../data/appGlobals/appGlobalsSelectors'
import { getCustomerName, getIsBlendedCareMedsAvailable } from '../../data/customer/customerSelectors'
import { trackEventWithObj } from '../../data/mixpanel'
import { TrackEventWithObjAction } from '../../data/mixpanel/types'
import { getIsCustomerDynamicSessionLimitEnabled } from '../../data/shared/sharedSelectors'

/*
ApplicationContext is used to add context to ui-core-crossplatform. In particular CustomerName and domain are currently
used for opening links in ui-core-crossplatform
*/
function ApplicationContext({
  customerName,
  detectedCountryIso,
  children,
  trackEventWithObj,
}: {
  customerName: string
  detectedCountryIso: string
  children: React.ReactNode
  trackEventWithObj: TrackEventWithObjAction
}) {
  const domain = process.env.NODE_ENV !== 'production' ? 'staging.lyrahealth.com' : 'lyrahealth.com'
  const copyPasteDisabled = false
  const trackEvent = (event: string, eventProps?: { [key in MIXPANEL_PROPERTY]?: any }) => {
    trackEventWithObj({
      event,
      ...eventProps,
    })
  }

  const sendExperimentData = useSendExperimentData()
  const isCustomerDynamicSessionLimitEnabled = useSelector(getIsCustomerDynamicSessionLimitEnabled)
  const isBlendedCareMedsAvailable = useSelector(getIsBlendedCareMedsAvailable)
  const { showAiCoach } = useShouldShowAICoach()
  const updateLDContext = useUpdateLDContext()

  const {
    isLatestMatchesEnabled,
    maxBenefitsToShowInExplorer,
    isClientDataShareEnabled,
    isCHCOutage,
    isHPIConsentWithMedicareEnabled,
    shouldShowLCPositioning,
    hideUserTestimonialsFromLCxModal,
    isDedicatedCareNavigatorEnabled,
    shouldSeePendoGuides,
    shouldShowCostEstimatesAsFullyCovered,
    isCostMessagingEnabled,
    isFamilyHubEnabled,
    isPreferredNameEnabled,
    shouldShowNewMinimizedIntegratedBenefitsDesign,
    showPickedForYouV2,
    showPickedForYouV3,
    routeUserDirectlyToAgentInInAppMessagingChatWindow,
    chatSessionTimeoutDuration,
    shouldShowAvailabilityPreference,
    isPhoneNumberRequiredForApptRequest,
    forceAdultFlowsToHPI,
    showCostTransparency,
    emailCapturePageExperimentV2,
    parentTeenBookingImprovementsEnabled,
    canSelectMultiplePrimaryNeeds,
    shouldShowConfirmationPageCapacityChecks,
    isSelectWellnessToolkitsRound2Enabled,
    areGlobalPreferencesEnabled,
    isHideCostAndSessionLimitEnabled,
    shouldHideEligibilityTypeQuestion,
    isCustomerTerminationModalEnabled,
    isAiInTriageEnabledThreeArm,
    isNewBenefitsPageDesignEnabled,
    showParentLedLCTTeenBookingUpdates,
    isInProductCalendarModalityEnabledS4C,
    isClientDisabled,
    isBenefitsExtensionBannerDisplayed,
    areExpandedGlobalPreferencesEnabled,
    isLyraForLyrians2026Enabled,
    isToolkitDirectoryPageEnabled,
    isPromotionalSMSEnabled,
    isProviderLevelFeedbackEnabled,
    showProviderAdjectives,
    isOnsiteEntrypointsEnabled,
    shouldPopulateApptPhoneNumber,
    isNeuroPilotEnabled,
    isLCTTeensAdditionalRemindersEnabled,
    isUniversalSearchEnabled: isSmartSearchEnabled,
    isAmazonCostTransparencyEnabled,
    isGooglePlacesAPIEnabled,
    isDisasterResponseServiceEnabled,
  } = useFlags()

  // Update LaunchDarkly context with isAiInTriageEnabledThreeArm so that
  // triageScreenersMetadataVersion can use it
  useEffect(() => {
    if (isAiInTriageEnabledThreeArm) {
      updateLDContext({ isAiInTriageEnabledThreeArm })
    }
  }, [isAiInTriageEnabledThreeArm, updateLDContext])

  const onNavbarLogoDefaultPress = useCallback(() => {
    window.location.href = '/'
  }, [])

  return (
    <AppContext.Provider
      value={{
        customerName,
        detectedCountryIso,
        domain,
        copyPasteDisabled,
        setModalStatusOpen: noop,
        isLatestMatchesEnabled,
        trackEvent,
        maxBenefitsToShowInExplorer,
        isClientDataShareEnabled,
        isCHCOutage,
        isHPIConsentWithMedicareEnabled,
        shouldShowLCPositioning,
        hideUserTestimonialsFromLCxModal,
        isDedicatedCareNavigatorEnabled,
        shouldSeePendoGuides,
        shouldShowCostEstimatesAsFullyCovered,
        isCostMessagingEnabled,
        isCustomerDynamicSessionLimitEnabled,
        isFamilyHubEnabled,
        isPreferredNameEnabled,
        shouldShowNewMinimizedIntegratedBenefitsDesign,
        showPickedForYouV2,
        showPickedForYouV3,
        routeUserDirectlyToAgentInInAppMessagingChatWindow,
        chatSessionTimeoutDuration,
        shouldShowAvailabilityPreference,
        isPhoneNumberRequiredForApptRequest,
        forceAdultFlowsToHPI,
        showCostTransparency,
        emailCapturePageExperimentV2,
        sendExperimentData,
        parentTeenBookingImprovementsEnabled,
        canSelectMultiplePrimaryNeeds,
        shouldShowConfirmationPageCapacityChecks,
        isSelectWellnessToolkitsRound2Enabled,
        areGlobalPreferencesEnabled,
        showAiCoach,
        isHideCostAndSessionLimitEnabled,
        shouldHideEligibilityTypeQuestion,
        isCustomerTerminationModalEnabled,
        isAiInTriageEnabledThreeArm,
        isNewBenefitsPageDesignEnabled,
        showParentLedLCTTeenBookingUpdates,
        isInProductCalendarModalityEnabledS4C,
        isClientDisabled,
        isBenefitsExtensionBannerDisplayed,
        areExpandedGlobalPreferencesEnabled,
        isLyraForLyrians2026Enabled,
        isToolkitDirectoryPageEnabled,
        isPromotionalSMSEnabled,
        showProviderAdjectives,
        isProviderLevelFeedbackEnabled,
        isOnsiteEntrypointsEnabled,
        shouldPopulateApptPhoneNumber,
        isNeuroPilotEnabled,
        isLCTTeensAdditionalRemindersEnabled,
        isSmartSearchEnabled,
        isAmazonCostTransparencyEnabled,
        isBlendedCareMedsAvailable,
        isGooglePlacesAPIEnabled,
        isDisasterResponseServiceEnabled,
        onNavbarLogoDefaultPress,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export default connect(
  (state) => ({ customerName: getCustomerName(state), detectedCountryIso: getDetectedCountryISO(state) }),
  { trackEventWithObj },
)(ApplicationContext)
